import React from 'react';
import { Tooltip, Typography } from 'antd';
import { CheckOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { translate } from '@portal/services/i18n';
import { getUserRoleDisplay } from '@portal/utils/getUserRoleDisplay';
import { hasAccess } from '@portal/utils/hasAccess';
import RolesType from '@portal/enum/rolesType';

import './UserCard.less';
import useReduxState from '@portal/hooks/useReduxState';

const { Text } = Typography;

interface UserCardProps {
  user: {
    id?: string;
    name?: string;
    login?: string;
    taxIdentifier?: string;
    role?: string;
    status?: string;
  };
  type: 'accepted' | 'pending' | 'sent';
  roles: string[];
  onClick?: () => void;
  onAccept?: () => void;
  onReject?: () => void;
  clickable?: boolean;
  onCancelInvite?: () => void;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  type,
  roles,
  onClick,
  onAccept,
  onReject,
  clickable = false,
  onCancelInvite,
}) => {
  const {
    theme: { currentTheme },
  } = useReduxState();

  const renderActions = () => {
    if (type === 'pending' && hasAccess(roles, RolesType.MANAGE_ACCOUNTS)) {
      return (
        <div className="user-card__actions">
          <button
            className="user-card__action-btn user-card__action-btn--accept"
            onClick={(e) => {
              e.stopPropagation();
              onAccept?.();
            }}
            title={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.ACCEPT_REQUEST')}
          >
            <CheckOutlined />
          </button>
          <button
            className="user-card__action-btn user-card__action-btn--reject"
            onClick={(e) => {
              e.stopPropagation();
              onReject?.();
            }}
            title={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.REJECT_REQUEST')}
          >
            <CloseCircleOutlined />
          </button>
        </div>
      );
    }

    if (type === 'sent' && hasAccess(roles, RolesType.MANAGE_ACCOUNTS)) {
      return (
        <div className="user-card__actions">
          <Tooltip
            title={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.CANCEL_INVITE')}
          >
            <button
              className="user-card__action-btn user-card__action-btn--reject"
              onClick={(e) => {
                e.stopPropagation();
                onCancelInvite?.();
              }}
              title={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.CANCEL_INVITE')}
            >
              <CloseCircleOutlined />
            </button>
          </Tooltip>
        </div>
      );
    }

    return null;
  };

  const renderDetails = () => {
    switch (type) {
      case 'pending':
        return (
          <>
            <div className="user-card__detail-item">
              <Text type="secondary">
                {translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.LOGIN')}: {user.login}
              </Text>
            </div>
            <div className="user-card__detail-item">
              <Text type="secondary">CPF/CNPJ: {user.taxIdentifier}</Text>
            </div>
          </>
        );
      case 'accepted':
      case 'sent':
      default:
        return (
          <>
            <div className="user-card__detail-item">
              <Text type="secondary">
                {translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.LOGIN')}: {user.login}
              </Text>
            </div>
            <div className="user-card__detail-item">
              <Text type="secondary">
                {translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.ROLE')}:{' '}
                {getUserRoleDisplay(user.role || '')}
              </Text>
            </div>
          </>
        );
    }
  };

  return (
    <div
      className={`user-card ${clickable ? 'user-card--clickable' : ''}`}
      style={{
        border: `1px solid ${currentTheme.borderColor}`,
      }}
      onClick={clickable ? onClick : undefined}
    >
      <div className="user-card__header">
        <div className="user-card__avatar">
          <div
            className="user-card__avatar__initials"
            style={{
              background: currentTheme.background,
            }}
          >
            {user.name?.charAt(0)?.toUpperCase()}
          </div>
        </div>
        <div className="user-card__content">
          <div className="user-card__title">
            <Text strong style={{ fontSize: '16px' }}>
              {user.name}
            </Text>
            {renderActions()}
          </div>
        </div>
      </div>
      <div className="user-card__details">{renderDetails()}</div>
    </div>
  );
};

export default UserCard;
